import { useAsyncData } from '#imports'
import { type MaybeRefOrGetter, computed, toRef } from 'vue'

import type { AlgoliaProduct } from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import algoliasearch from 'algoliasearch'

import { algoliaHitToVariant } from '../../helpers/recommendationHelper'

import type {
  AlgoliaRecommendationsModel,
  AlgoliaRecommendationsObjectId,
  AlgoliaRecommendationsOptions,
  ViewedItemFromAlgoliaResults,
} from './useAlgoliaRecommendations.types'
import {
  buildCompatibilityFilters,
  buildFallbackCompatibilityFilters,
} from './useAlgoliaRecommendations.utils'

export function useAlgoliaRecommendations(
  objectID: MaybeRefOrGetter<AlgoliaRecommendationsObjectId>,
  model: AlgoliaRecommendationsModel,
  options?: MaybeRefOrGetter<AlgoliaRecommendationsOptions | undefined>,
) {
  const objectIDRef = toRef(objectID)
  const optionsRef = toRef(options)

  const $httpFetch = useHttpFetch()

  const { data: configurationData } = useAsyncData(
    'algoliaRecommendationsConfig',
    () => $httpFetch('/bm/recommendation/configuration'),
    { dedupe: 'defer' },
  )

  const queryKey = computed(() =>
    JSON.stringify({
      indexName: configurationData.value?.indexName,
      objectID: objectIDRef.value,
      model,
      ...optionsRef.value,
    }),
  )

  return useAsyncData(
    queryKey.value,
    async () => {
      if (!objectIDRef.value || !configurationData.value?.indexName) return null

      const algoliaSearchOption = useAlgoliaSearchOptions('recommendations')
      const algoliaClient = algoliasearch(
        'dummy_app_id',
        'dummy_key',
        algoliaSearchOption,
      )

      let mergedOptions = optionsRef.value

      if (model === 'bought-together') {
        const viewedItemFromAlgolia: ViewedItemFromAlgoliaResults =
          await algoliaClient.multipleGetObjects([
            {
              indexName: configurationData.value?.indexName,
              objectID: objectIDRef.value,
            },
          ])

        const {
          compatibility_family: compatibilityFamily,
          compatibility_product_model: compatibilityProductModel,
          compatibility_connectivity: compatibilityConnectivity,
        } = viewedItemFromAlgolia.results[0]

        const compatibilityFilterAttributes = [
          {
            key: 'compatibility_family',
            values: compatibilityFamily,
            score: 3,
          },
          {
            key: 'compatibility_product_model',
            values: compatibilityProductModel,
            score: 5,
          },
          {
            key: 'compatibility_connectivity',
            values: compatibilityConnectivity,
          },
        ]

        const hasCompatibilityValues = compatibilityFilterAttributes.some(
          ({ values }) => values.length,
        )

        if (hasCompatibilityValues) {
          const existingFilters = optionsRef.value?.queryParameters?.filters
          const existingFallbackFilters =
            optionsRef.value?.fallbackParameters?.filters

          const compatibilityFilters = buildCompatibilityFilters(
            compatibilityFilterAttributes,
          )

          const fallbackCompatibilityFilters =
            buildFallbackCompatibilityFilters(compatibilityFilterAttributes)

          mergedOptions = {
            ...optionsRef.value,
            queryParameters: {
              ...optionsRef.value?.queryParameters,
              filters: existingFilters
                ? `${compatibilityFilters} AND (${existingFilters})`
                : compatibilityFilters,
            },
            fallbackParameters: {
              ...optionsRef.value?.fallbackParameters,
              filters: existingFallbackFilters
                ? `${fallbackCompatibilityFilters} AND (${existingFallbackFilters})`
                : fallbackCompatibilityFilters,
              sumOrFiltersScores: true,
            },
            threshold: optionsRef.value?.threshold ?? 50,
          }
        }
      }

      return algoliaClient.getRecommendations<AlgoliaProduct>([
        {
          indexName: configurationData.value?.indexName,
          objectID: objectIDRef.value,
          model,
          ...mergedOptions,
        },
      ])
    },
    {
      lazy: true,
      server: false,
      dedupe: 'defer',
      default: () => [],
      deep: false,
      watch: [queryKey],
      transform: (response) => {
        if (!response) {
          return []
        }

        const { results } = response

        return results[0]?.hits.map((hit) => algoliaHitToVariant(hit))
      },
    },
  )
}
